import type {
  IRoutedWatchTicketAnswer,
  MachineWatchStateType,
} from '@featit/shared/terminal-watch';
// The reasons alone, not the watch's rules: the launch entry reaches this API (T-0770, the entry budget).
import {
  isTerminalUnavailableReason,
  type TerminalUnavailableReasonType,
} from '@featit/shared/terminal-unavailable-reasons';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { ApiService } from './ApiService';

/**
 * F-0714 RLY-129: the desktop app's device-key proof for a ticket request, loaded only in the
 * desktop app, so the page's entry does not carry the signer.
 */
async function appProofField(): Promise<Record<string, unknown>> {
  if (!isDesktopRuntime()) return {};
  return (await import('./relayAppProof')).withRelayAppProof();
}

/** What the deployment hands the owner for one watch (ADR-112). */
export interface IIssuedWatchTicket {
  ticket: string;
  /** `wss://<name>.ts.net:<port>/watch` — checked with `isWatchAddress` before it is opened. */
  address: string;
  expiresAt: string;
  /** F-0342: whether this ticket also lets its holder type, not only watch. */
  canInput: boolean;
}

/**
 * Watch tickets and each machine's browser-viewing state (F-0225).
 *
 * REST rather than GraphQL, because both are writes with side effects the deployment decides on:
 * issuing records a ticket, and reporting clears an address.
 */
export class TerminalWatchApiService extends ApiService {
  /**
   * A ticket for one of your sessions. Rejects with the reason a viewer is told. Asked with
   * `readsRoutes`, the answer carries the machine's routes and may have no address (F-0714 RLY-119).
   */
  async issueTicket(input: {
    machineId: string;
    provider: string;
    sessionRef: string;
    readsRoutes?: boolean;
    workAreaId?: string;
  }): Promise<IIssuedWatchTicket | IRoutedWatchTicketAnswer> {
    try {
      return await this.post<IIssuedWatchTicket | IRoutedWatchTicketAnswer>(
        '/terminal-watch/tickets',
        { ...input, ...(await appProofField()) },
      );
    } catch (error) {
      // F-0720: the response stays under the reason, so the connection journal reads its status.
      throw Object.assign(new Error(refusalReason(error)), { cause: error });
    }
  }

  /**
   * What this machine says about browser viewing. `off` clears its address on the deployment, and
   * its `watch` capability; the key it reports stays registered (ADR-175).
   *
   * ⭐ I-1516: also the machine sync of its watch limit. It says the limit its hub applies and is
   * answered with the one its owner set (absent from an older deployment). U-0023: and which routes
   * its listener serves, when the desktop says so.
   */
  reportMachineWatch(
    machineId: string,
    state: MachineWatchStateType,
    address: string | null,
    redeemKey: string | null,
    appliedLimit: number | null = null,
    serves: readonly string[] | null = null,
  ): Promise<{ state: MachineWatchStateType; watchLimit?: number }> {
    return this.put(`/terminal-watch/machines/${encodeURIComponent(machineId)}`, {
      state,
      // ⭐ D-0347, ADR-175: the key is registered with a report of any state, so Off and the start
      // setting reach the deployment while watching is off. The address is only ever an `on`'s.
      ...(state === 'on' ? { address } : {}),
      ...(redeemKey ? { redeemKey } : {}),
      ...(appliedLimit === null ? {} : { appliedLimit }),
      // U-0023 (FG-84): only what the desktop said. Left out for an older one, which the
      // deployment then reads as serving no files (FG-75).
      ...(serves ? { serves: [...serves] } : {}),
    });
  }

  // F-0342 security review, finding 3 (AC-49): there is no recordInputEvent here any more. The
  // audit record now comes from the desktop process that actually ran the send, machine-signed,
  // on POST /terminal-watch/machine/input-events — a route this client, authenticated only as a
  // person, is never the one calling. See TerminalInputField.tsx's send().
}

/**
 * The deployment names why a machine serves no ticket (409); anything else is the deployment's own
 * answer: a sign-in it did not take, a refusal, a failure, or none at all (F-0720).
 */
function refusalReason(error: unknown): TerminalUnavailableReasonType {
  const response = (error as { response?: { status?: number; data?: { reason?: unknown } } })
    ?.response;
  const reason = response?.data?.reason;
  if (response?.status === 409 && isTerminalUnavailableReason(reason)) return reason;
  // F-0720 (MD-31): any other answer is the deployment's, not the machine's, read by its status as the
  // connection journal reads it (`ticketNotAnsweredReasonOf`). *Mutation:* read a 5xx as `refused` →
  // TerminalWatchApi.test "a ticket the deployment failed to give says so, not that the machine
  // refused" red.
  const status = response?.status;
  if (typeof status !== 'number') return 'deployment-unreachable';
  if (status === 401) return 'ticket-signed-out';
  return status >= 500 ? 'deployment-error' : 'deployment-refused';
}

export const terminalWatchApi = new TerminalWatchApiService();
